// OrgUnitForm.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useDataQuery, useDataMutation } from '@dhis2/app-runtime';
import { useAuth } from '../../auth/AuthContext';
import {
  Button,
  InputField,
  SingleSelect,
  SingleSelectOption,
  CircularLoader,
  AlertBar,
  IconAdd16,
  IconFullscreenExit16
} from '@dhis2/ui';
import { useOrgUnits } from '../hooks/useOrgUnits';
import { useNavigate } from 'react-router-dom';
import { assignUserToOrgs } from '../../../utils/api';
import DepartmentManager from './DepartmentManager';

// Constants
const ATTRIBUTE_IDS = {
  ORG_EMAIL: 'P1KhiQpaXO4',
  ORG_TYPE: 'WxaXz5yCf4f',
  FINANCIAL_START: 'jYywVrwX55m',   
  FINANCIAL_END: 'Amtp5VXvduY'      
};

const ORG_TYPE_QUERY = {
  orgTypes: {
    resource: 'optionSets/z6JnM4OcF11',
    params: { fields: 'options[id,name,code]' }
  }
};

const CURRENT_USER_QUERY = {
  me: {
    resource: 'me',
    params: { fields: 'id,username,organisationUnits[id,name]' }
  }
};

 const useFinancialDateValidation = (startDate, endDate) => {
  const [errors, setErrors] = useState({});
    useEffect(() => {
      const newErrors = {};
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (end <= start) {
          newErrors.dateRange = 'End date must be after start date';
        }
        // Optional: validate ~12 months
        const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        if (diffMonths < 11 || diffMonths > 13) {
          newErrors.duration = 'Financial year should be approximately 12 months';
        }
      }
      setErrors(newErrors);
    }, [startDate, endDate]);
    return errors;
  };

// Helper for generating code
function generateOrgCode(name) {
  const initials = name.trim()
    .split(/\s+/)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .padEnd(3, 'X')
    .slice(0, 3);
  return `OU_${initials}_${Date.now().toString().slice(-4)}`;
}

// Mutations
const MUTATIONS = {
  CREATE_ORG_UNIT: {
    resource: 'organisationUnits',
    type: 'create',
    data: ({ orgUnit }) => ({
      name: orgUnit.name.trim(),
      shortName: orgUnit.name.trim().substring(0, 50),
      openingDate: orgUnit.openingDate,
      level: orgUnit.level,
      parent: orgUnit.parent,
      attributeValues: orgUnit.attributeValues,
      code: orgUnit.code || generateOrgCode(orgUnit.name)
    }),
    params: { fields: 'id,name,level,code,created' }
  },
  DELETE_ORG_UNIT: {
    resource: 'organisationUnits',
    type: 'delete',
    id: ({ id }) => id
  }
};

const OrgUnitForm = () => {
  const navigate = useNavigate();
  const { completeRegistration } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [notification, setNotification] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdOrgUnitId, setCreatedOrgUnitId] = useState(null);
  const [formData, setFormData] = useState({
    continentId: '',
    countryId: '',
    regionId: '',
    organisationName: '',
    organisationType: '',
    orgEmail: '',
    financialYearStart: '',   
    financialYearEnd: '',     
    departments: [],
    newDepartment: ''
  });

  const [createOrgUnit] = useDataMutation(MUTATIONS.CREATE_ORG_UNIT);
  const [deleteOrgUnit] = useDataMutation(MUTATIONS.DELETE_ORG_UNIT);

  const financialDateErrors = useFinancialDateValidation(formData.financialYearStart, formData.financialYearEnd);

  const { data: currentUserData, loading: userLoading } = useDataQuery(CURRENT_USER_QUERY);
  const { getOrgUnitsByLevel, getOrgUnitsByParent, loading: orgUnitsLoading } = useOrgUnits();
  const { data: orgTypeData, loading: orgTypesLoading } = useDataQuery(ORG_TYPE_QUERY);

  const continent = useMemo(() => getOrgUnitsByLevel(2), [getOrgUnitsByLevel]);
  const countries = useMemo(() => getOrgUnitsByLevel(3), [getOrgUnitsByLevel]);
  const regions = useMemo(() => formData.countryId ? getOrgUnitsByParent(formData.countryId) : [], [formData.countryId, getOrgUnitsByParent]);
  const orgTypes = useMemo(() => orgTypeData?.orgTypes?.options || [], [orgTypeData]);

  const TOP_LEVEL_ORG_ID = 'YbzM5Sy37YS';
  const userHasRealOrg = (user) => Array.isArray(user.organisationUnits) && user.organisationUnits.some(ou => ou.id !== TOP_LEVEL_ORG_ID);

  if (userLoading) return <CircularLoader />;
  if (currentUserData?.me && userHasRealOrg(currentUserData.me)) return null;

  // Add Department
  const addDepartment = () => {
    if (formData.newDepartment.trim()) {
      setFormData(prev => ({
        ...prev,
        departments: [...prev.departments, { id: Date.now(), name: prev.newDepartment.trim(), newSection: '', sections: [] }],
        newDepartment: ''
      }));
    }
  };

 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setNotification(null);

    let newOrgUnitId = null;

    try {

      // Validate financial dates
      if (formData.financialYearStart && formData.financialYearEnd) {
        const startDate = new Date(formData.financialYearStart);
        const endDate = new Date(formData.financialYearEnd);
        if (endDate <= startDate) {
          setNotification({ critical: true, message: 'Financial year end date must be after start date' });
          setSubmitting(false);
          return;
        }
      }

      const requiredFields = {
        'Organization name': formData.organisationName,
        'Region': formData.regionId,
        'Organization type': formData.organisationType,
        'Organisation Email': formData.orgEmail
      };
      const missingFields = Object.entries(requiredFields).filter(([_, value]) => !(value || '').trim()).map(([name]) => name);
      if (missingFields.length > 0) throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      if (!currentUserData?.me?.id) throw new Error("Could not determine current user information");

      // 1. Create main org unit
      const orgPayload = {
        name: formData.organisationName.trim(),
        shortName: formData.organisationName.trim().substring(0, 50),
        openingDate: new Date().toISOString().split('T')[0],
        parent: { id: formData.regionId },
        attributeValues: [
          { attribute: { id: ATTRIBUTE_IDS.ORG_TYPE }, value: formData.organisationType },
          formData.orgEmail && { attribute: { id: ATTRIBUTE_IDS.ORG_EMAIL }, value: formData.orgEmail.trim() },
          formData.financialYearStart && { attribute: { id: ATTRIBUTE_IDS.FINANCIAL_START }, value: formData.financialYearStart },
          formData.financialYearEnd && { attribute: { id: ATTRIBUTE_IDS.FINANCIAL_END }, value: formData.financialYearEnd }
        ].filter(Boolean)
      };

      const orgResponse = await createOrgUnit({ orgUnit: orgPayload });
      newOrgUnitId = orgResponse.response?.uid || orgResponse.uid;
      setCreatedOrgUnitId(newOrgUnitId);

      // 2. Create departments → sections → subSections
      for (const dept of formData.departments) {
        const deptResp = await createOrgUnit({
          orgUnit: {
            name: dept.name,
            shortName: dept.name.substring(0, 50),
            parent: { id: newOrgUnitId },
            openingDate: new Date().toISOString().split('T')[0]
          }
        });
        const deptId = deptResp.response?.uid || deptResp.uid;
        dept.createdId = deptId;

        for (const sec of dept.sections || []) {
          const secResp = await createOrgUnit({
            orgUnit: {
              name: sec.name,
              shortName: sec.name.substring(0, 50),
              parent: { id: deptId },
              openingDate: new Date().toISOString().split('T')[0]
            }
          });
          const secId = secResp.response?.uid || secResp.uid;
          sec.createdId = secId;

          for (const sub of sec.subSections || []) {
            const subResp = await createOrgUnit({
              orgUnit: {
                name: sub.name,
                shortName: sub.name.substring(0, 50),
                parent: { id: secId },
                openingDate: new Date().toISOString().split('T')[0]
              }
            });
            sub.createdId = subResp.response?.uid || subResp.uid;
          }
        }
      }

      // 3. Assign user to all units
          const allUnits = [
          { id: newOrgUnitId, name: formData.organisationName },
          ...formData.departments.flatMap(d => [
            { id: d.createdId, name: d.name },
            ...d.sections.flatMap(s => [
              { id: s.createdId, name: s.name },
              ...s.subSections.map(ss => ({ id: ss.createdId, name: ss.name }))
            ])
          ])
        ];
        ////HERE***********************************************
      await assignUserToOrgs(currentUserData.me.id, allUnits);

      completeRegistration(true);
      setNotification({ success: true, message: 'Organization created successfully! Redirecting...' });
      setTimeout(() => navigate('/dashboard'), 2000);

    } catch (error) {
      console.error("Submission failed:", error);
      setNotification({ critical: true, message: error.message });

      if (createdOrgUnitId) {
        try { await deleteOrgUnit({ id: createdOrgUnitId }); } 
        catch (rollbackError) { console.error('Rollback failed:', rollbackError); }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!showForm ? (
        <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '60px', boxSizing: 'border-box' }}>
          <Button 
            primary 
            onClick={() => setShowForm(true)} 
            icon={<IconAdd16 />}
            data-test="create-org-button"
          >
            Create your Organization
          </Button>
        </div>
      ) : (
        <div style={{ padding: '24px', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#f8f8f8' }} data-test="org-form-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3>New Organization Unit</h3>
            <Button small secondary onClick={() => setShowForm(false)} icon={<IconFullscreenExit16 />}>Close</Button>
          </div>

          {notification && (
            <AlertBar critical={notification.critical} success={notification.success} onHidden={() => setNotification(null)}>
              {notification.message}
            </AlertBar>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label>Continent</label>
                <SingleSelect
                  selected={formData.continentId}
                  onChange={({ selected }) => setFormData(prev => ({ ...prev, continentId: selected, countryId: '', regionId: '' }))}
                  loading={orgUnitsLoading}
                  filterable
                >
                  {continent.map(cont => <SingleSelectOption key={cont.id} value={cont.id} label={cont.name} />)}
                </SingleSelect>
              </div>

              <div>
                <label>Country</label>
                <SingleSelect
                  selected={formData.countryId}
                  onChange={({ selected }) => setFormData(prev => ({ ...prev, countryId: selected, regionId: '' }))}
                  loading={orgUnitsLoading}
                  filterable
                >
                  {countries.map(c => <SingleSelectOption key={c.id} value={c.id} label={c.name} />)}
                </SingleSelect>
              </div>

              {formData.countryId && (
                <div>
                  <label>Region</label>
                  <SingleSelect
                    selected={formData.regionId}
                    onChange={({ selected }) => setFormData(prev => ({ ...prev, regionId: selected }))}
                    loading={orgUnitsLoading}
                    filterable
                  >
                    {regions.map(r => <SingleSelectOption key={r.id} value={r.id} label={r.name} />)}
                  </SingleSelect>
                </div>
              )}

              {formData.regionId && (
                <>
                  <div>
                    <label>Organization Name</label>
                    <InputField value={formData.organisationName} onChange={({ value }) => setFormData(prev => ({ ...prev, organisationName: value }))} required />
                  </div>

                  <div>
                    <label>Organization Email</label>
                    <InputField type="email" value={formData.orgEmail} onChange={({ value }) => setFormData(prev => ({ ...prev, orgEmail: value }))} validationText="Organization contact email" />
                  </div>

                  <div>
                    <label>Organization Type</label>
                    <SingleSelect selected={formData.organisationType} onChange={({ selected }) => setFormData(prev => ({ ...prev, organisationType: selected }))} loading={orgTypesLoading} required>
                      {orgTypes.map(type => <SingleSelectOption key={type.id} value={type.id} label={type.name} />)}
                    </SingleSelect>
                  </div>
                  {/* Financial Calendar Section */}
                <div style={{
                  gridColumn: '1 / -1',
                  marginBottom: '20px',
                  padding: '20px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Financial/Business Calendar</h3>
                  <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                    Set your organization's financial year. This will be used for goal setting, reporting, and performance cycles.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label>Financial Year Start Date</label>
                      <InputField
                        type="date"
                        value={formData.financialYearStart}
                        onChange={({ value }) => setFormData(prev => ({ ...prev, financialYearStart: value }))}
                        required
                        error={!!financialDateErrors.dateRange}
                        validationText={financialDateErrors.dateRange}
                      />
                    </div>
                    <div>
                      <label>Financial Year End Date</label>
                      <InputField
                        type="date"
                        value={formData.financialYearEnd}
                        onChange={({ value }) => setFormData(prev => ({ ...prev, financialYearEnd: value }))}
                        required
                        error={!!financialDateErrors.dateRange}
                        validationText={financialDateErrors.dateRange}
                      />
                    </div>
                  </div>
                  {financialDateErrors.duration && (
                    <AlertBar warning style={{ marginTop: '10px' }}>
                      Note: {financialDateErrors.duration}
                    </AlertBar>
                  )}
                </div>
                </>
              )}
            </div>

            {formData.regionId && (
              <>
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <InputField value={formData.newDepartment} onChange={({ value }) => setFormData(prev => ({ ...prev, newDepartment: value }))} placeholder="Department name" fullWidth />
                    <Button small onClick={addDepartment} disabled={!formData.newDepartment.trim()}>Add Department</Button>
                  </div>
                </div>

                {formData.departments.length > 0 && (
                  <DepartmentManager
                    departments={formData.departments}
                    onRemoveDepartment={(deptId) => setFormData(prev => ({ ...prev, departments: prev.departments.filter(d => d.id !== deptId) }))}
                    onUpdateDepartment={(deptId, action, payload) => {
                      setFormData(prev => ({
                        ...prev,
                        departments: prev.departments.map(d => {
                          if (d.id !== deptId) return d;
                          switch(action) {
                            case 'addSection':
                              if(!d.newSection?.trim()) return d;
                              return { ...d, sections: [...(d.sections || []), { id: Date.now(), name: d.newSection, newSubSection: '', subSections: [] }], newSection: '' };
                            case 'removeSection':
                              return { ...d, sections: (d.sections || []).filter(s => s.id !== payload) };
                            case 'newSection':
                              return { ...d, newSection: payload };
                            case 'updateSection':
                              const { sectionId, action: subAction, value } = payload;
                              return { ...d, sections: d.sections.map(s => {
                                if (s.id !== sectionId) return s;
                                switch(subAction) {
                                  case 'addSubSection':
                                    if(!s.newSubSection?.trim()) return s;
                                    return { ...s, subSections: [...(s.subSections || []), { id: Date.now(), name: s.newSubSection }], newSubSection: '' };
                                  case 'removeSubSection':
                                    return { ...s, subSections: (s.subSections || []).filter(ss => ss.id !== value) };
                                  case 'updateSubSectionInput':
                                    return { ...s, newSubSection: value };
                                  default: return s;
                                }
                              })};
                            default: return d;
                          }
                        })
                      }));
                    }}
                  />
                )}
              </>
            )}

            <div style={{ marginTop: '24px' }}>
              <Button primary type="submit" disabled={submitting}>{submitting ? <CircularLoader small /> : 'Create Organization'}</Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default OrgUnitForm;
